'use client';

import type Player from '@vimeo/player';
import { AnimatePresence, motion } from 'motion/react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Media, MediaBlock } from '@/payload-types';
import { cn } from '@/utils/cn';

import { ImageMedia } from '../ImageMedia';
import styles from './VideoMedia.module.scss';
import { VideoModal } from './VideoModal';
import { VimeoPlayPauseButton } from './VimeoPlayPauseButton';

export type VideoMediaHandle = {
  play: () => void;
  pause: () => void;
};

type Props = {
  className?: string;
  videoPoster: Media;
  videoEmbedUrl: string;
  videoIsBackground?: boolean;
  videoPlayerClassName?: string;
  hidePlayPause?: boolean;
  videoLoops?: boolean;
  priority?: boolean;
  imageResponsiveWidths?: {
    mobile: number;
    desktop: number;
    largeDesktop: number;
  };
  aspectRatio: NonNullable<MediaBlock['medias']>[number]['aspectRatio'];
  delayed?: boolean;
  playOnHover?: boolean;
};

export const VideoMedia = forwardRef<VideoMediaHandle, Props>(
  (
    {
      aspectRatio,
      className,
      delayed = false,
      hidePlayPause = false,
      imageResponsiveWidths,
      playOnHover = false,
      priority,
      videoEmbedUrl,
      videoIsBackground,
      videoLoops,
      videoPlayerClassName,
      videoPoster,
    },
    ref
  ) => {
    const playerRef = useRef<Player | null>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const observerTargetRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const hasIntersectedRef = useRef(false);

    const [videoReady, setVideoReady] = useState(false);
    const [player, setPlayer] = useState<Player | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Expose control to parent
    useImperativeHandle(ref, () => ({
      play: () => {
        playerRef.current?.play().catch(console.error);
      },
      pause: () => {
        playerRef.current?.pause().catch(console.error);
      },
    }));

    // Lazy player creation on intersection
    useEffect(() => {
      if (!observerTargetRef.current || delayed) return;

      const createPlayer = async () => {
        if (playerRef.current || !playerContainerRef.current) return;

        const videoIdMatch = videoEmbedUrl.match(/vimeo\.com\/(\d+)/);
        const videoId = videoIdMatch ? parseInt(videoIdMatch[1]) : null;
        if (!videoId) return;

        try {
          const { initVimeoPlayer, loadVimeoScript } = await import(
            '@/utils/loadVimeoScript'
          );
          await loadVimeoScript();

          const newPlayer = await initVimeoPlayer(playerContainerRef.current, {
            id: videoId,
            background: !hidePlayPause
              ? true
              : playOnHover
                ? false
                : videoIsBackground,
            autoplay: playOnHover ? false : true,
            muted: playOnHover ? true : false,
            controls: playOnHover ? false : true,
            loop: videoLoops,
          });

          playerRef.current = newPlayer;
          setPlayer(newPlayer);

          newPlayer.ready().then(() => {
            setTimeout(() => setVideoReady(true), 600);
          });

          if (playOnHover) {
            newPlayer.on('mouseover', () =>
              newPlayer.play().catch(console.error)
            );
            newPlayer.on('mouseout', () =>
              newPlayer.pause().catch(console.error)
            );
          }
        } catch (error) {
          console.error('Failed to initialize Vimeo player:', error);
        }
      };

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasIntersectedRef.current) {
            hasIntersectedRef.current = true;
            createPlayer().catch(console.error);
            observerRef.current?.disconnect();
          }
        },
        { threshold: 0.4 }
      );

      observerRef.current.observe(observerTargetRef.current);

      return () => {
        observerRef.current?.disconnect();
      };
    }, [
      delayed,
      videoEmbedUrl,
      videoIsBackground,
      playOnHover,
      videoLoops,
      hidePlayPause,
    ]);

    // Pause/Play based on visibility (non-hover background videos)
    useEffect(() => {
      if (
        !player ||
        (hidePlayPause && playOnHover) ||
        !observerTargetRef.current
      )
        return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            player.play().catch(console.error);
          } else {
            player.pause().catch(console.error);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(observerTargetRef.current);

      return () => {
        observer.disconnect();
      };
    }, [hidePlayPause, playOnHover, player]);

    // Modal handlers
    const handlePlayPause = () => {
      if (!player) return;
      player
        .getPaused()
        .then((paused) => (paused ? player.play() : player.pause()));
    };

    const handleModalOpen = () => {
      setModalOpen(true);
      document.body.style.overflow = 'hidden';
    };

    const handleModalClose = () => {
      setModalOpen(false);
      document.body.style.overflow = 'auto';
    };

    // Cleanup
    useEffect(() => {
      return () => {
        playerRef.current?.destroy().catch(console.error);
        observerRef.current?.disconnect();
        setPlayer(null);
        setVideoReady(false);
        playerRef.current = null;
      };
    }, []);

    return (
      <div
        className={cn(
          styles.wrapper,
          aspectRatio ? styles[aspectRatio] : '',
          className
        )}
      >
        {videoIsBackground ? (
          <AnimatePresence>
            <motion.div
              key='poster'
              initial={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'flex',
                flexGrow: 1,
                width: '100%',
                willChange: 'opacity',
              }}
            >
              <ImageMedia
                aspectRatio={aspectRatio}
                image={videoPoster}
                priority={priority}
                className={styles.media}
                responsiveWidths={
                  imageResponsiveWidths || aspectRatio === 'square'
                    ? {
                        mobile: 343,
                        desktop: 512,
                        largeDesktop: 2256,
                      }
                    : aspectRatio === 'portrait'
                      ? {
                          mobile: 343,
                          desktop: 512,
                          largeDesktop: 800,
                        }
                      : {
                          mobile: 343,
                          desktop: 1352,
                          largeDesktop: 2256,
                        }
                }
              />
            </motion.div>
            <motion.div
              key='video'
              initial={{ opacity: 0 }}
              animate={{ opacity: videoReady ? 1 : 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              style={{ display: 'flex', flexGrow: 1, willChange: 'opacity' }}
              className={cn(styles.videoContainer)}
              ref={observerTargetRef}
            >
              <div
                ref={playerContainerRef}
                style={{ pointerEvents: 'none' }}
                className={cn(styles.videoPlayer, videoPlayerClassName)}
              />
              {!hidePlayPause && player && (
                <VimeoPlayPauseButton
                  large={!videoIsBackground}
                  center={!videoIsBackground}
                  player={player}
                  clickHandler={handlePlayPause}
                />
              )}
            </motion.div>
          </AnimatePresence>
        ) : (
          <>
            <div>
              <ImageMedia
                aspectRatio={aspectRatio}
                image={videoPoster}
                priority={priority}
                responsiveWidths={
                  imageResponsiveWidths || aspectRatio === 'square'
                    ? {
                        mobile: 343,
                        desktop: 512,
                        largeDesktop: 2256,
                      }
                    : aspectRatio === 'portrait'
                      ? {
                          mobile: 343,
                          desktop: 512,
                          largeDesktop: 800,
                        }
                      : {
                          mobile: 343,
                          desktop: 1352,
                          largeDesktop: 2256,
                        }
                }
              />
              <VimeoPlayPauseButton
                large={!videoIsBackground}
                center={!videoIsBackground}
                opensModal
                clickHandler={handleModalOpen}
              />
            </div>
            <AnimatePresence>
              {modalOpen && (
                <VideoModal
                  videoEmbedUrl={videoEmbedUrl}
                  clickHandler={handleModalClose}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    );
  }
);
VideoMedia.displayName = 'VideoMedia';
